/**
 * Lectura de listas de usuarios desde CSV / TSV / Excel para los bulks de
 * reactivación de Founders.
 *
 * Devuelve siempre un array plano de strings (uid, email o @usuario); quién
 * es cada uno lo resuelve el worker en /reactivations/resolve.
 *
 * Estrategia de columnas: si el archivo tiene varias, nos quedamos con la que
 * más celdas "identificables" tiene (email o algo con pinta de uid). Así da
 * igual que el export lleve columnas de nombre, plan o fecha al lado.
 */

const UID_RE = /^[A-Za-z0-9_-]{20,64}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function looksIdentifiable(value) {
    const v = String(value || '').trim();
    if (!v) return false;
    return EMAIL_RE.test(v) || UID_RE.test(v) || v.startsWith('@');
}

/**
 * Parser CSV mínimo pero correcto: respeta comillas dobles, comillas
 * escapadas ("") y saltos de línea dentro de campos entrecomillados.
 * Detecta el separador entre coma, punto y coma y tabulador.
 */
export function parseDelimited(text) {
    const sample = text.slice(0, 5000);
    const counts = {
        ',': (sample.match(/,/g) || []).length,
        ';': (sample.match(/;/g) || []).length,
        '\t': (sample.match(/\t/g) || []).length
    };
    const delimiter = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][1] > 0
        ? Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0]
        : ',';

    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
        const ch = text[i];

        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                field += ch;
            }
            continue;
        }

        if (ch === '"') {
            inQuotes = true;
        } else if (ch === delimiter) {
            row.push(field);
            field = '';
        } else if (ch === '\n') {
            row.push(field);
            rows.push(row);
            row = [];
            field = '';
        } else if (ch !== '\r') {
            field += ch;
        }
    }
    row.push(field);
    rows.push(row);

    return rows.filter((r) => r.some((c) => String(c || '').trim() !== ''));
}

/**
 * De una matriz de celdas a la lista de identificadores: elige la columna con
 * más valores identificables y devuelve sus celdas no vacías. Si ninguna
 * columna destaca (por ejemplo, una lista pegada a pelo sin cabecera),
 * devuelve todas las celdas identificables en orden de lectura.
 */
export function pickIdentifierColumn(rows) {
    if (!rows.length) return [];

    const width = Math.max(...rows.map((r) => r.length));
    let bestIndex = -1;
    let bestScore = 0;

    for (let col = 0; col < width; col++) {
        let score = 0;
        for (const r of rows) if (looksIdentifiable(r[col])) score++;
        if (score > bestScore) {
            bestScore = score;
            bestIndex = col;
        }
    }

    if (bestIndex === -1) {
        // Sin nada reconocible: nos quedamos con la primera columna no vacía y
        // que el worker diga cuáles no existen.
        return rows.map((r) => String(r.find((c) => String(c || '').trim()) || '').trim()).filter(Boolean);
    }

    const picked = rows
        .map((r) => String(r[bestIndex] || '').trim())
        .filter(Boolean)
        // Descarta la celda de cabecera ("uid", "email", "correo"…).
        .filter((v) => looksIdentifiable(v) || !/^(uid|id|email|correo|user|usuario|tiktok)$/i.test(v));

    return picked;
}

/** Texto pegado a mano en el textarea → lista de identificadores. */
export function parsePastedList(text) {
    return String(text || '')
        .split(/[\n,;\t]+/)
        .map((s) => s.trim().replace(/^["']|["']$/g, ''))
        .filter(Boolean);
}

/**
 * Lee un File del input. Soporta .csv/.tsv/.txt de forma nativa y
 * .xlsx/.xls/.ods vía SheetJS, que se carga solo cuando hace falta.
 */
export async function readIdentifiersFromFile(file) {
    const name = (file?.name || '').toLowerCase();
    const isSpreadsheet = /\.(xlsx|xlsm|xlsb|xls|ods)$/.test(name);

    if (isSpreadsheet) {
        const XLSX = await import('xlsx');
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        if (!sheet) return [];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, raw: false });
        return pickIdentifierColumn(rows.map((r) => (Array.isArray(r) ? r : [r])));
    }

    const text = await file.text();
    return pickIdentifierColumn(parseDelimited(text));
}

export const IMPORT_ACCEPT = '.csv,.tsv,.txt,.xlsx,.xlsm,.xlsb,.xls,.ods';
